import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import useClickOutside from '../../hooks/useClickOutside';
import usePinchZoom, { PinchRange } from './usePinchZoom';
import {
  MONTH_NAMES,
  RangeKey,
  formatInputDate,
  formatRangeDate,
  packLanes,
  rangePresets,
  rangeToDays,
} from './timelineUtils';

export interface TimelineItem {
  id: string | number;
  start: Date | null;
  end: Date | null;
}

interface TimelineProps<T extends TimelineItem> {
  items: T[];
  title: string;
  icon: ReactNode;
  /** Optional legend / subtitle rendered under the title. */
  legend?: ReactNode;
  /** Status / theme classes for the bar background of a given item. */
  barClassName: (item: T) => string;
  /** Inner content of the bar (badges, labels, etc.). */
  renderBarContent: (item: T) => ReactNode;
  /**
   * Optional: group items into stacked rows instead of the default one-row-per-item layout.
   * Items sharing a key form a group ordered by key (numbers ascending); each group is packed
   * into the fewest rows its overlaps require, so non-overlapping items share the group's first
   * row and overlaps spill onto extra rows.
   */
  laneGroupKey?: (item: T) => number | string;
  /**
   * Minimum on-screen width (px) reserved for a bar, used with laneGroupKey so bars whose
   * label is wider than their time span don't visually overlap — a too-close neighbour is
   * pushed to a new row. Pass a function for a per-item width (e.g. measured from the
   * item's label when the label overflows the bar).
   */
  laneMinBarPx?: number | ((item: T) => number);
  /**
   * Optional: predefined ordered lane groups (used with laneGroupKey). Every declared group
   * renders at least one row — empty groups show as empty labeled rows — and the group's label
   * is pinned as a chip at the left of its first row. Items whose key matches no declared group
   * are appended as extra groups at the end.
   */
  laneGroups?: { key: number | string; label?: string }[];
  /**
   * Notified when the committed visible range changes (mount, preset/custom change, pinch
   * commit — not during transient pinch frames). Lets the parent fetch data per range.
   */
  onRangeChange?: (start: Date, end: Date) => void;
  /**
   * Optional time ranges flagged as problems (e.g. two rentals booked on the same lock):
   * each renders as a translucent red full-height band behind the bars.
   */
  conflictRanges?: { start: Date; end: Date }[];
  /**
   * Optional: items sharing a non-null key (e.g. the bars of one multi-lock rental)
   * are visually tied together by a thin vertical string with a dot on each bar,
   * drawn at their common start time across the rows.
   */
  connectKey?: (item: T) => string | number | null;
  /**
   * Optional extra classes for a connect string, evaluated on its topmost item —
   * e.g. fade the strings of finished items with an opacity class.
   */
  connectorClassName?: (item: T) => string;
  /**
   * Optional solid CSS color for a connect string (line + dots), evaluated on its
   * topmost item — e.g. the item's status color so the string blends with its bars.
   * Must be opaque; soften via connectorClassName's group opacity.
   */
  connectorColor?: (item: T) => string;
  emptyLabel?: string;
  defaultRange?: RangeKey;
  /** Persist the selected range to the URL query string (range / start / end). */
  urlSync?: boolean;
  onBarClick?: (item: T) => void;
  /** Coordinates are relative to the timeline card. */
  onBarHover?: (item: T, x: number, y: number) => void;
  onBarLeave?: () => void;
  onBarContextMenu?: (item: T, x: number, y: number) => void;
  /** Optional overlay (tooltip, context menu) rendered inside the card. */
  children?: ReactNode;
  onBackgroundClick?: () => void;
  total?: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

function Timeline<T extends TimelineItem>({
  items,
  title,
  icon,
  legend,
  barClassName,
  renderBarContent,
  laneGroupKey,
  laneMinBarPx = 0,
  laneGroups,
  onRangeChange,
  conflictRanges,
  connectKey,
  connectorClassName,
  connectorColor,
  emptyLabel = 'Aucun élément sur cette période',
  defaultRange = '30d',
  urlSync,
  onBarClick,
  onBarHover,
  onBarLeave,
  onBarContextMenu,
  children,
  onBackgroundClick,
  total,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: TimelineProps<T>) {
  const cardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // Date range state — seeded from the URL when urlSync is enabled.
  const initialRange = useMemo(() => {
    if (urlSync) {
      const startParam = searchParams.get('start');
      const endParam = searchParams.get('end');
      if (startParam && endParam) {
        const start = new Date(`${startParam}T00:00:00`);
        const end = new Date(`${endParam}T23:59:59`);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
          return { range: defaultRange, custom: { start, end } };
        }
      }
      const rangeParam = searchParams.get('range');
      if (rangeParam && rangeParam in rangeToDays) {
        return { range: rangeParam as RangeKey, custom: null };
      }
    }
    return { range: defaultRange, custom: null as { start: Date; end: Date } | null };
    // Seed once on mount; subsequent changes flow through the setters below.
  }, []);

  const [selectedRange, setSelectedRange] = useState<RangeKey>(initialRange.range);
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date } | null>(initialRange.custom);
  // Transient window during a pinch gesture; overrides the preset/custom range.
  const [liveRange, setLiveRange] = useState<{ start: Date; end: Date } | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const closePopover = useCallback(() => setPopoverOpen(false), []);
  useClickOutside(popoverRef, closePopover, popoverOpen);

  const isCustomRange = customRange !== null;

  const { startDate, endDate } = useMemo(() => {
    if (liveRange) {
      return { startDate: liveRange.start, endDate: liveRange.end };
    }
    if (customRange) {
      return { startDate: customRange.start, endDate: customRange.end };
    }
    const now = new Date();
    if (selectedRange === '1d') {
      // 24h window centered on now, snapped to the hour
      const start = new Date(now);
      start.setMinutes(0, 0, 0);
      start.setHours(start.getHours() - 12);
      const end = new Date(now);
      end.setMinutes(0, 0, 0);
      end.setHours(end.getHours() + 12);
      return { startDate: start, endDate: end };
    }
    const totalDays = rangeToDays[selectedRange];
    const daysBefore = Math.floor((totalDays - 1) / 2);
    const start = new Date(now);
    start.setDate(start.getDate() - daysBefore);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setDate(end.getDate() + (totalDays - 1 - daysBefore));
    end.setHours(23, 59, 59, 999);
    return { startDate: start, endDate: end };
  }, [selectedRange, customRange, liveRange]);

  const [customStart, setCustomStart] = useState(formatInputDate(startDate));
  const [customEnd, setCustomEnd] = useState(formatInputDate(endDate));

  const handleRangeChange = (key: RangeKey) => {
    setSelectedRange(key);
    setCustomRange(null);
    setPopoverOpen(false);
    if (urlSync) {
      const next = new URLSearchParams(searchParams);
      next.set('range', key);
      next.delete('start');
      next.delete('end');
      setSearchParams(next, { replace: true });
    }
  };

  const handlePopoverToggle = () => {
    if (!popoverOpen) {
      setCustomStart(formatInputDate(startDate));
      setCustomEnd(formatInputDate(endDate));
    }
    setPopoverOpen(!popoverOpen);
  };

  const handleApply = () => {
    const start = new Date(customStart + 'T00:00:00');
    const end = new Date(customEnd + 'T23:59:59');
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return;
    setCustomRange({ start, end });
    setPopoverOpen(false);
    if (urlSync) {
      const next = new URLSearchParams(searchParams);
      next.set('start', customStart);
      next.set('end', customEnd);
      next.delete('range');
      setSearchParams(next, { replace: true });
    }
  };

  // Horizontal plot geometry. With laneGroups, the left padding widens into a
  // gutter where the group labels live, OUTSIDE the plotted area, so they never
  // cover the start of the timeline. Every time→x mapping (axis, grid, bands,
  // connectors, pinch anchor) goes through these values.
  const padLeftPx = laneGroups ? 64 : 20;
  const padRightPx = 20;
  const plotX = (percent: number) => `calc(${padLeftPx}px + (100% - ${padLeftPx + padRightPx}px) * ${percent / 100})`;

  // Geometry changes (items appearing after a pan commit, lanes re-packing,
  // heights shifting) settle with a short transition — but NOT during a live
  // pan/pinch, where positions update every frame and a transition would lag.
  const geomTransition = liveRange ? '' : 'transition-[left,width,height,top] duration-200';

  const rangeRef = useRef<PinchRange>({ start: startDate.getTime(), end: endDate.getTime() });
  rangeRef.current = { start: startDate.getTime(), end: endDate.getTime() };

  const handlePinchChange = useCallback((range: PinchRange) => {
    setLiveRange({ start: new Date(range.start), end: new Date(range.end) });
  }, []);

  const handlePinchCommit = useCallback(
    (range: PinchRange) => {
      const start = new Date(range.start);
      const end = new Date(range.end);
      setCustomRange({ start, end });
      setLiveRange(null);
      if (urlSync) {
        const next = new URLSearchParams(searchParams);
        next.set('start', formatInputDate(start));
        next.set('end', formatInputDate(end));
        next.delete('range');
        setSearchParams(next, { replace: true });
      }
    },
    [urlSync, searchParams, setSearchParams],
  );

  usePinchZoom(containerRef, {
    rangeRef,
    onChange: handlePinchChange,
    onCommit: handlePinchCommit,
    padding: { left: padLeftPx, right: padRightPx },
  });

  // Notify the parent of committed range changes only — transient pinch frames
  // (liveRange) would spam refetches. The callback lives in a ref so an inline
  // lambda prop doesn't retrigger the effect.
  const onRangeChangeRef = useRef(onRangeChange);
  onRangeChangeRef.current = onRangeChange;
  useEffect(() => {
    if (!liveRange) onRangeChangeRef.current?.(startDate, endDate);
  }, [startDate, endDate, liveRange]);

  // Timeline computed from date range
  const { timelineStart, timelineEnd, ticks, hourly } = useMemo(() => {
    const spanMs = endDate.getTime() - startDate.getTime();
    const isHourly = spanMs <= 2 * 24 * 60 * 60 * 1000;
    const list: Date[] = [];
    const d = new Date(startDate);
    if (isHourly) {
      d.setMinutes(0, 0, 0);
      if (d < startDate) d.setHours(d.getHours() + 1);
      while (d <= endDate) {
        list.push(new Date(d));
        d.setHours(d.getHours() + 1);
      }
    } else {
      while (d <= endDate) {
        list.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
    }

    return {
      timelineStart: startDate,
      timelineEnd: endDate,
      ticks: list,
      hourly: isHourly,
    };
  }, [startDate, endDate]);

  const totalMs = timelineEnd.getTime() - timelineStart.getTime();

  // Filter items that overlap with the timeline
  const visibleItems = useMemo(() => {
    return items.filter((item) => {
      const start = item.start ? item.start.getTime() : 0;
      const end = item.end ? item.end.getTime() : Date.now() + 86400000;
      return start < timelineEnd.getTime() && end > timelineStart.getTime();
    });
  }, [items, timelineStart, timelineEnd]);

  // Measure the plotting width so a bar's minimum label width can be expressed as a time span.
  const [plotWidth, setPlotWidth] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setPlotWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Minimum on-screen footprint of a bar, converted from px to ms at the current zoom.
  const minBarMs = useMemo<number | ((item: T) => number)>(() => {
    if (plotWidth <= 0 || !laneMinBarPx) return 0;
    if (typeof laneMinBarPx === 'number') return (laneMinBarPx / plotWidth) * totalMs;
    return (item: T) => (laneMinBarPx(item) / plotWidth) * totalMs;
  }, [plotWidth, laneMinBarPx, totalMs]);

  // Row groups to render: grouped/packed lanes when laneGroupKey is set, else one
  // single-lane group per item. With laneGroups, every declared group gets at least
  // one (possibly empty) labeled lane; a group needing several lanes subdivides the
  // same fixed height, so every group stays equally tall.
  const rowGroups = useMemo<{ lanes: T[][]; label?: string }[]>(() => {
    if (!laneGroupKey) return visibleItems.map((item) => ({ lanes: [[item]] }));
    const groups = new Map<number | string, T[]>();
    for (const item of visibleItems) {
      const key = laneGroupKey(item);
      const group = groups.get(key);
      if (group) group.push(item);
      else groups.set(key, [item]);
    }
    if (laneGroups) {
      const declaredKeys = new Set(laneGroups.map((group) => group.key));
      const extraKeys = Array.from(groups.keys())
        .filter((key) => !declaredKeys.has(key))
        .sort((a, b) => (typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))));
      return [
        ...laneGroups.map((group) => ({ key: group.key, label: group.label })),
        ...extraKeys.map((key) => ({ key, label: undefined as string | undefined })),
      ].map(({ key, label }) => {
        const lanes = packLanes(groups.get(key) ?? [], minBarMs);
        if (lanes.length === 0) return { lanes: [[]] as T[][], label };
        return { lanes, label };
      });
    }
    return Array.from(groups.keys())
      .sort((a, b) => (typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))))
      .map((key) => ({ lanes: packLanes(groups.get(key)!, minBarMs) }));
  }, [visibleItems, laneGroupKey, laneGroups, minBarMs]);

  // Vertical layout of the groups, shared by the rows and the connector
  // overlay. Every group is equally tall (BASE_LANE_PX + padding); overlap
  // lanes subdivide it down to MIN_SUBLANE_PX, beyond which (3+ overlaps,
  // rare) the group grows instead of becoming unreadable.
  const BASE_LANE_PX = 32;
  const MIN_SUBLANE_PX = 16;
  const GROUP_PADDING_Y = 4; // py-1
  const GROUP_GAP = 4; // container gap-1
  const PLOT_PADDING_TOP = 12; // pt-3

  const groupLayouts = useMemo(() => {
    let topPx = PLOT_PADDING_TOP;
    return rowGroups.map((group) => {
      const laneHeightPx = Math.max(BASE_LANE_PX / group.lanes.length, MIN_SUBLANE_PX);
      const contentHeightPx = laneHeightPx * group.lanes.length;
      const layout = { laneHeightPx, contentHeightPx, topPx };
      topPx += contentHeightPx + GROUP_PADDING_Y * 2 + GROUP_GAP;
      return layout;
    });
  }, [rowGroups]);

  // Bracket tying together the bars that share a connect key (one multi-lock
  // rental): a vertical line just LEFT of the bars' start, with a short
  // horizontal stub hooking into each bar's left edge at its vertical center —
  //    ┌────┐
  // ┌──│bar │
  // │  └────┘
  // │  ┌────┐
  // └──│bar │
  //    └────┘
  const connectors = useMemo(() => {
    if (!connectKey || !laneGroups) return [];
    const byKey = new Map<string | number, { xPct: number; yCenters: number[]; firstItem: T }>();
    rowGroups.forEach((group, groupIdx) => {
      const layout = groupLayouts[groupIdx];
      group.lanes.forEach((lane, laneIdx) => {
        lane.forEach((item) => {
          const key = connectKey(item);
          if (key == null) return;
          const startMs = Math.max(item.start ? item.start.getTime() : timelineStart.getTime(), timelineStart.getTime());
          if (startMs >= timelineEnd.getTime()) return;
          const entry = byKey.get(key) ?? {
            xPct: ((startMs - timelineStart.getTime()) / totalMs) * 100,
            yCenters: [] as number[],
            firstItem: item,
          };
          entry.yCenters.push(layout.topPx + GROUP_PADDING_Y + laneIdx * layout.laneHeightPx + layout.laneHeightPx / 2);
          byKey.set(key, entry);
        });
      });
    });
    return Array.from(byKey.entries())
      .filter(([, entry]) => entry.yCenters.length > 1)
      .map(([key, entry]) => ({
        key,
        xPct: entry.xPct,
        firstItem: entry.firstItem,
        ys: [...entry.yCenters].sort((a, b) => a - b),
      }));
  }, [rowGroups, groupLayouts, connectKey, laneGroups, timelineStart, timelineEnd, totalMs]);

  const getBarStyle = (item: T) => {
    const start = item.start
      ? Math.max(item.start.getTime(), timelineStart.getTime())
      : timelineStart.getTime();
    const end = item.end
      ? Math.min(item.end.getTime(), timelineEnd.getTime())
      : timelineEnd.getTime();

    const left = ((start - timelineStart.getTime()) / totalMs) * 100;
    const width = ((end - start) / totalMs) * 100;

    return {
      left: `${left}%`,
      width: `${Math.max(width, 0.5)}%`,
      // A 1h rental on a 30-day view must still read as a bar, not a sliver.
      minWidth: 8,
    };
  };

  const handleMouseEnter = (e: React.MouseEvent, item: T) => {
    if (!onBarHover) return;
    const cardRect = cardRef.current?.getBoundingClientRect();
    const barRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (!cardRect) return;
    const x = barRect.left + barRect.width / 2 - cardRect.left;
    const y = barRect.top - cardRect.top;
    onBarHover(item, x, y);
  };

  const handleContextMenu = (e: React.MouseEvent, item: T) => {
    if (!onBarContextMenu) return;
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    onBarContextMenu(item, e.clientX - rect.left, e.clientY - rect.top);
  };

  // The today marker fades in/out with the window instead of unmounting (its
  // row keeps its height), so panning past today never makes the layout jump.
  const now = new Date();
  const showTodayMarker = now >= timelineStart && now <= timelineEnd;
  const todayPercent = Math.max(0, Math.min(100, ((now.getTime() - timelineStart.getTime()) / totalMs) * 100));
  const todayFade = `duration-200 ${liveRange ? 'transition-opacity' : 'transition-[left,opacity]'} ${showTodayMarker ? 'opacity-100' : 'opacity-0'}`;

  // For dense axes, skip some labels to avoid clutter
  const labelInterval = hourly
    ? ticks.length > 36
      ? 6
      : ticks.length > 18
        ? 3
        : 2
    : ticks.length > 60
      ? 7
      : ticks.length > 14
        ? 2
        : 1;

  return (
    <div ref={cardRef} className="bg-white rounded-xl shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 relative overflow-visible p-4 flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-3">
            {icon}
            <h3 className="typo-h3 text-neutral">{title}</h3>
          </div>
          {legend}
        </div>
        {/* Date range controls */}
        <div className="relative" ref={popoverRef}>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePopoverToggle}
              className={`pl-3 pr-8 py-1.5 rounded-md border border-neutral-10 flex items-center gap-1.5 cursor-pointer transition-colors typo-body relative ${
                isCustomRange
                  ? 'bg-neutral text-white'
                  : 'bg-white text-neutral-60 hover:text-neutral hover:bg-neutral-5'
              }`}
            >
              <span>{formatRangeDate(startDate)} - {formatRangeDate(endDate)}</span>
              <ChevronDownIcon className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
                isCustomRange ? 'text-white' : 'text-neutral-40'
              }`} strokeWidth={3} />
            </button>
            {rangePresets.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={`px-2.5 py-1 rounded-md typo-small transition-colors ${
                  !isCustomRange && selectedRange === preset.key
                    ? 'bg-neutral text-white font-semibold cursor-default'
                    : 'text-neutral-60 hover:text-neutral'
                }`}
                onClick={() => handleRangeChange(preset.key)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom range popover */}
          {popoverOpen && (
            <div
              className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg p-4 flex flex-col gap-3"
              style={{
                width: 320,
                boxShadow: '0px 8px 30px rgba(0,0,0,0.14)',
                border: '1px solid rgba(26,26,26,0.08)',
              }}
            >
              <span className="typo-small text-neutral-40 font-bold uppercase tracking-wider">
                Période personnalisée
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="flex-1 px-2.5 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                />
                <span className="text-neutral-30 typo-small">→</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="flex-1 px-2.5 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closePopover}
                  className="px-2.5 py-1 typo-small text-neutral-40 hover:text-neutral transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  className="px-3 py-1 rounded-md bg-primary-500 text-white typo-small font-semibold hover:opacity-90 transition-opacity"
                >
                  Appliquer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div
        ref={containerRef}
        className="relative w-full flex flex-col"
        style={{ touchAction: 'pan-y' }}
        onClick={onBackgroundClick}
      >
        {/* Today label — always mounted (constant height), fades with the window */}
        <div className="w-full relative h-6" style={{ paddingLeft: padLeftPx, paddingRight: padRightPx }}>
          <div
            className={`absolute -translate-x-1/2 z-20 ${todayFade}`}
            style={{ left: plotX(todayPercent) }}
          >
            <span className="bg-orange-100 px-2 py-1 rounded-md typo-caption !font-bold text-primary-500 whitespace-nowrap">
              Aujourd'hui
            </span>
          </div>
        </div>

        {/* Day axis */}
        <div className="w-full relative pt-8 pb-2 border-b border-neutral-10 h-6" style={{ paddingLeft: padLeftPx, paddingRight: padRightPx }}>
          {(() => {
            let prevMonth = -1;
            let prevDay = -1;
            return ticks.map((tick, i) => {
              if (i % labelInterval !== 0) return null;
              const percent = ((tick.getTime() - timelineStart.getTime()) / totalMs) * 100;
              let label: string;
              if (hourly) {
                const dayChanged = tick.getDate() !== prevDay;
                prevDay = tick.getDate();
                label = dayChanged
                  ? `${tick.getDate()} ${MONTH_NAMES[tick.getMonth()]} ${tick.getHours()}h`
                  : `${tick.getHours()}h`;
              } else {
                const showMonth = tick.getMonth() !== prevMonth;
                prevMonth = tick.getMonth();
                label = showMonth
                  ? `${MONTH_NAMES[tick.getMonth()]} ${tick.getDate()}`
                  : String(tick.getDate());
              }
              return (
                <span
                  key={i}
                  className={`absolute text-small text-neutral-40 text-nowrap -translate-x-1/2 ${geomTransition}`}
                  style={{ left: plotX(percent), bottom: 8 }}
                >
                  {label}
                </span>
              );
            });
          })()}
        </div>

        {/* Today highlight line — full height, at current time, fades with the window */}
        <div
          className={`absolute top-0 bottom-0 w-px bg-neutral-10 z-0 ${todayFade}`}
          style={{ left: plotX(todayPercent) }}
        />

        {/* Bars */}
        <div className="relative w-full min-h-[60px] pt-3 pb-4 flex flex-col gap-1" style={{ paddingLeft: padLeftPx, paddingRight: padRightPx }}>
          {/* Day grid lines */}
          {ticks.map((tick, i) => {
            if (hourly && i % labelInterval !== 0) return null;
            const percent = ((tick.getTime() - timelineStart.getTime()) / totalMs) * 100;
            return (
              <div
                key={`line-${i}`}
                className={`absolute top-0 bottom-0 w-px bg-neutral-5 z-0 ${geomTransition}`}
                style={{ left: plotX(percent) }}
              />
            );
          })}
          {/* Conflict bands — full-height translucent red columns behind the bars */}
          {(conflictRanges ?? []).map((range, i) => {
            const startPct = Math.max(0, Math.min(100, ((range.start.getTime() - timelineStart.getTime()) / totalMs) * 100));
            const endPct = Math.max(0, Math.min(100, ((range.end.getTime() - timelineStart.getTime()) / totalMs) * 100));
            if (endPct <= startPct) return null;
            return (
              <div
                key={`conflict-${i}`}
                className={`absolute top-0 bottom-0 bg-error-500 opacity-10 pointer-events-none z-0 ${geomTransition}`}
                style={{
                  left: plotX(startPct),
                  width: `calc((100% - ${padLeftPx + padRightPx}px) * ${(endPct - startPct) / 100})`,
                  minWidth: 4,
                }}
              />
            );
          })}
          {rowGroups.length === 0 && !laneGroups ? (
            <div className="py-8 text-center typo-small text-neutral-40">
              {emptyLabel}
            </div>
          ) : (
            // Zebra background per group (unpositioned wrapper, so the absolutely
            // positioned grid lines still paint above it) — a subtle guide to tell
            // one lock's row from the next.
            rowGroups.map((group, groupIdx) => {
              // The label spans the group incl. its vertical padding (anchored
              // in the first lane because the wrapper must stay unpositioned —
              // see zebra note above — hence the -4px offset).
              const { laneHeightPx, contentHeightPx } = groupLayouts[groupIdx];
              const groupHeightPx = contentHeightPx + GROUP_PADDING_Y * 2;
              return (
              <div
                key={`group-${groupIdx}`}
                // The inner padding gives the bars breathing room within their
                // zebra band; the container gap separates neighbouring locks.
                className={`flex flex-col py-1 ${laneGroups && groupIdx % 2 === 1 ? 'bg-[rgba(26,26,26,0.03)] rounded' : ''}`}
              >
                {group.lanes.map((lane, laneIdx) => (
                  <div key={`lane-${laneIdx}`} className={`relative ${geomTransition}`} style={{ height: laneHeightPx }}>
                    {laneIdx === 0 && group.label != null && (
                      // In the left gutter (outside the plotted area) so it
                      // never covers the start of the timeline.
                      <span
                        className={`absolute z-10 px-1 justify-center rounded bg-neutral text-white typo-caption !font-bold whitespace-nowrap overflow-hidden pointer-events-none flex items-center ${geomTransition}`}
                        style={{
                          top: -GROUP_PADDING_Y,
                          height: groupHeightPx,
                          left: -(padLeftPx - 8),
                          width: padLeftPx - 16,
                        }}
                      >
                        {group.label}
                      </span>
                    )}
                    {lane.map((item) => {
                      const barStyle = getBarStyle(item);
                      // z-20 keeps every bar above the connector brackets
                      // (z-[15]) that may cross its row.
                      const barZ = 'z-20';
                      return (
                        <div
                          key={item.id}
                          className={`absolute top-0 h-full rounded-sm cursor-pointer flex items-center px-0.5 gap-1 overflow-visible hover:opacity-90 ${liveRange ? 'transition-opacity' : 'transition-[left,width,opacity] duration-200'} ${barZ} ${barClassName(item)}`}
                          style={barStyle}
                          onClick={onBarClick ? () => onBarClick(item) : undefined}
                          onMouseEnter={(e) => handleMouseEnter(e, item)}
                          onMouseLeave={onBarLeave}
                          onContextMenu={(e) => handleContextMenu(e, item)}
                        >
                          {renderBarContent(item)}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              );
            })
          )}
          {/* Strings tying the bars of one connect-key group (multi-lock rental)
              across rows: a vertical line at their common start + a dot per bar. */}
          {/* Solid colors (no alpha) with any fading applied as group opacity on
              the wrapper (connectorClassName), so the vertical line and its stubs
              never darken each other where they join. The bracket sits left of
              the bars, so it never covers its own items. */}
          {connectors.map((connector) => {
            const color = connectorColor?.(connector.firstItem) ?? '#BABABA';
            return (
              <div
                key={`connector-${connector.key}`}
                className={`absolute inset-y-0 z-[15] pointer-events-none ${geomTransition} ${connectorClassName?.(connector.firstItem) ?? ''}`}
                style={{ left: plotX(connector.xPct) }}
              >
                {/* The bracket is one bordered box (left + top + bottom edges,
                    rounded left corners): top/bottom borders are the hooks into
                    the first and last bars; a straight stub serves each
                    intermediate bar. */}
                <div
                  className={`absolute rounded-l-md ${geomTransition}`}
                  style={{
                    left: -7,
                    width: 7,
                    top: connector.ys[0] - 1,
                    height: connector.ys[connector.ys.length - 1] - connector.ys[0] + 2,
                    borderLeft: `2px solid ${color}`,
                    borderTop: `2px solid ${color}`,
                    borderBottom: `2px solid ${color}`,
                  }}
                />
                {connector.ys.slice(1, -1).map((y, stubIdx) => (
                  <div
                    key={stubIdx}
                    className={`absolute h-0.5 ${geomTransition}`}
                    style={{ left: -7, width: 7, top: y - 1, backgroundColor: color }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Load more */}
      {hasNextPage && (
        <div className="flex items-center justify-between pt-2 border-t border-neutral-10">
          <span className="typo-small text-neutral-40">
            {items.length}{total != null ? ` / ${total}` : ''}
          </span>
          <button
            type="button"
            disabled={isFetchingNextPage}
            onClick={onLoadMore}
            className="px-2.5 py-1 rounded-md typo-small text-neutral-60 hover:text-neutral hover:bg-neutral-5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {isFetchingNextPage ? 'Chargement...' : 'Charger plus'}
          </button>
        </div>
      )}

      {/* Consumer overlay (tooltip, context menu) */}
      {children}
    </div>
  );
}

export default Timeline;
